<script setup lang="ts">
/**
 * Equipo de la cuenta, con el mismo formato que la libreta de clientes
 * (`pages/clients/index.vue`, portada de `pages/apps/user/list/index.vue` de la
 * plantilla): filtros y buscador en una línea, y tabla con avatar, chips de
 * estado y acciones por fila. **Sin el resumen de métricas** de clientes: el
 * equipo son unidades y los contadores no dicen nada que la tabla no muestre.
 *
 * A diferencia de clientes, `GET /team` devuelve **todo el equipo de una vez**
 * (son unidades, no cientos), así que la tabla es `VDataTable` y el filtrado, la
 * búsqueda y la paginación son de cliente: recargar por tipear una letra sería
 * un viaje al servidor para reordenar una lista que ya está en memoria.
 */
import { providerRoleTooltip } from '@/data/providerRoles'
import { computed, onMounted, ref, watch } from 'vue'
import { listDiveCenters } from '@/api/dive-centers'
import {
  listTeam,
  createStaff,
  updateTeamMember,
  removeTeamMember,
  transferOwnership,
  type TeamMember,
} from '@/api/team'
import { useAuthStore } from '@/stores/auth'
import { ApiError } from '@/api/client'
import AlertBox from '@/components/AlertBox.vue'
import MemberFormModal, { type MemberFormPayload } from '@/components/MemberFormModal.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'
import type { DiveCenter, ProviderUserRole } from '@/types'

definePage({ meta: { requiresAuth: true } })

const auth = useAuthStore()

const loading = ref(true)
const error = ref('')

const members = ref<TeamMember[]>([])
const diveCenters = ref<DiveCenter[]>([])

// Opciones de la tabla, con los nombres que espera `VDataTable`.
const itemsPerPage = ref(10)
const page = ref(1)
const selectedRows = ref<string[]>([])

const search = ref('')
const roleFilter = ref<ProviderUserRole | null>(null)
const centerFilter = ref<string | null>(null)

/*
 * Un solo formulario para alta y edición. `formMember` en null = alta; con un
 * miembro, edición. Tener dos modales separados era lo que hacía que la clase de
 * ficha quedara decidida para siempre en el momento del alta.
 */
const showForm = ref(false)
const formMember = ref<TeamMember | null>(null)
const formSubmitting = ref(false)
const formError = ref('')

const transferTarget = ref<string | null>(null)
const transferSubmitting = ref(false)

const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)

const actorRole = computed(() => auth.providerUser?.role)
const actorId = computed(() => auth.providerUser?.id)
const isOwner = computed(() => actorRole.value === 'owner')

const headers = [
  { title: 'Member', key: 'member' },
  { title: 'Role', key: 'role', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Dive centers', key: 'centers', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false },
]

const roleItems = [
  { title: 'Owner', value: 'owner' },
  { title: 'Admin', value: 'admin' },
  { title: 'Manager', value: 'manager' },
  { title: 'Staff', value: 'staff' },
]

const centerItems = computed(() =>
  diveCenters.value.map(dc => ({ title: dc.name, value: dc.id })),
)

const centerNameById = computed(() => {
  const map = new Map<string, string>()

  diveCenters.value.forEach(dc => map.set(dc.id, dc.name))

  return map
})

const fullName = (m: TeamMember) => `${m.firstName} ${m.lastName ?? ''}`.trim()

/** Identificador legible: el staff de directorio no tiene mail. */
const memberLabel = (m: TeamMember) => m.email || fullName(m)

/** Resumen de la ficha de staff para el listado. */
function staffSummary(m: TeamMember) {
  const p = m.staffProfile
  if (!p)
    return ''

  return [p.jobTitle, p.certificationLevel].filter(Boolean).join(' · ')
}

/**
 * Segunda línea de la celda del nombre: el mail identifica a quien entra al
 * portal; a quien no, lo define lo que hace a bordo.
 */
const memberSubtitle = (m: TeamMember) => m.email || staffSummary(m) || '—'

const centersOf = (m: TeamMember) =>
  (m.assignedDiveCenterIds || []).map(id => centerNameById.value.get(id)).filter(Boolean) as string[]

// Owner y admin ven todos los centros por rol, así que su lista de asignados
// está vacía y decir "ninguno" sería exactamente lo contrario de la verdad.
const seesAllCenters = (m: TeamMember) => m.role === 'owner' || m.role === 'admin'

function statusChip(m: TeamMember) {
  if (m.status === 'pending_invite')
    return { label: 'Pending invite', color: 'warning' }
  if (m.status === 'suspended')
    return { label: 'Suspended', color: 'error' }

  // Gris y no rojo: no entrar al portal es lo normal para el staff, no un
  // problema. El rojo se reserva para algo que requiera acción.
  if (m.status === 'no_access')
    return { label: 'No portal access', color: 'secondary' }

  return { label: 'Active', color: 'success' }
}

/*
 * El rol solo se muestra si la persona entra al portal: para una ficha de
 * directorio, "Staff" describía permisos que no ejerce y competía con el rol a
 * bordo, que es lo que de verdad la define. Ese va en la celda del nombre.
 */
const roleLabel = (m: TeamMember) => m.role.charAt(0).toUpperCase() + m.role.slice(1)

const filteredMembers = computed(() => {
  const q = search.value.trim().toLowerCase()

  return members.value.filter(m => {
    if (roleFilter.value && m.role !== roleFilter.value)
      return false

    // El filtro por centro incluye a quien lo ve por rol: buscar "quién trabaja
    // en este centro" y que no aparezca el owner sería una lista incompleta.
    if (centerFilter.value
      && !seesAllCenters(m)
      && !m.assignedDiveCenterIds?.includes(centerFilter.value))
      return false

    if (!q)
      return true

    return [fullName(m), m.email, staffSummary(m)]
      .filter(Boolean)
      .some(v => v!.toLowerCase().includes(q))
  })
})

// Quedarse en la página 3 de un resultado que ahora tiene una sola muestra la
// tabla vacía.
watch([search, roleFilter, centerFilter], () => { page.value = 1 })

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [t, d] = await Promise.all([listTeam(), listDiveCenters()])

    members.value = t.members
    diveCenters.value = d.diveCenters
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not load team.'
  }
  finally {
    loading.value = false
  }
}

// ---- Alta / edición -----------------------------------------------------
function startCreate() {
  formMember.value = null
  formError.value = ''
  showForm.value = true
}

function startEdit(m: TeamMember) {
  if (!canEdit(m))
    return

  formMember.value = m
  formError.value = ''
  showForm.value = true
}

async function submitForm(payload: MemberFormPayload) {
  formError.value = ''
  formSubmitting.value = true

  const editing = formMember.value

  try {
    if (editing) {
      await updateTeamMember(editing.id, {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        canLogin: payload.canLogin,
        role: payload.role,
        assignedDiveCenterIds: payload.assignedDiveCenterIds,
        staffProfile: payload.staffProfile,
      })
      notifySuccess('Member updated.')
    }
    else {
      await createStaff(payload)
      notifySuccess(
        payload.canLogin
          ? `Invitation sent to ${payload.email}.`
          : `${payload.firstName} ${payload.lastName} added to the team.`,
      )
    }

    showForm.value = false
    await load()
  }
  catch (e) {
    formError.value = e instanceof ApiError
      ? e.message
      : (editing ? 'Update failed.' : 'Could not add the member.')
  }
  finally {
    formSubmitting.value = false
  }
}

// ---- Remove -------------------------------------------------------------
async function remove(m: TeamMember) {
  const ok = await confirmRef.value?.open({
    title: 'Remove member',
    message: `Remove ${fullName(m)} (${memberLabel(m)}) from the team?`,
    confirmText: 'Remove',
    confirmColor: 'error',
  })
  if (!ok)
    return

  try {
    await removeTeamMember(m.id)
    // Se saca de la selección o la barra seguiría contando a alguien que ya no
    // está en la tabla.
    selectedRows.value = selectedRows.value.filter(id => id !== m.id)
    notifySuccess(`${memberLabel(m)} removed.`)
    await load()
  }
  catch (e) {
    notifyError(apiMessage(e, 'Remove failed.'))
  }
}

// ---- Transfer ownership -------------------------------------------------
async function doTransfer(m: TeamMember) {
  const ok = await confirmRef.value?.open({
    title: 'Transfer ownership',
    message:
      `Transfer ownership to ${fullName(m)} (${memberLabel(m)})? `
      + `You will become an admin afterwards. This cannot be undone from here.`,
    confirmText: 'Transfer ownership',
    confirmColor: 'primary',
  })
  if (!ok)
    return

  transferTarget.value = m.id
  transferSubmitting.value = true
  try {
    await transferOwnership(m.id)
    notifySuccess('Ownership transferred.')

    // El rol de quien opera cambió del lado del servidor. Se re-hidrata desde
    // /account/me para que el sidebar y los guards reflejen el nuevo rol.
    await auth.refreshMe()
    await load()
  }
  catch (e) {
    notifyError(apiMessage(e, 'Transfer failed.'))
  }
  finally {
    transferSubmitting.value = false
    transferTarget.value = null
  }
}

function canEdit(m: TeamMember) {
  if (m.role === 'owner')
    return false
  if (m.id === actorId.value)
    return false
  if (actorRole.value === 'admin' && m.role === 'admin')
    return false

  return actorRole.value === 'owner' || actorRole.value === 'admin'
}

// Mismas reglas que editar.
const canRemove = canEdit

function canTransferTo(m: TeamMember) {
  // El staff queda afuera aunque tenga acceso: la cuenta la administra quien
  // gestiona el negocio, no quien está asignado a las salidas.
  if (m.role === 'staff')
    return false

  return isOwner.value && m.status === 'active' && m.id !== actorId.value && m.role !== 'owner'
}
</script>

<template>
  <section>
    <!-- Solo el error de carga: el resultado de invitar, editar o quitar sale
         por el snackbar. -->
    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard class="mb-6">
      <!-- Filtros y buscador en la misma línea, como en la libreta de clientes:
           dos desplegables y un campo no justifican media pantalla. -->
      <VCardText class="d-flex flex-wrap align-center gap-4">
        <VSelect
          v-model="roleFilter"
          label="Role"
          placeholder="Any"
          :items="roleItems"
          density="compact"
          clearable
          clear-icon="ri-close-line"
          style="min-inline-size: 200px; max-inline-size: 240px;"
        />
        <VSelect
          v-model="centerFilter"
          label="Dive center"
          placeholder="Any"
          :items="centerItems"
          density="compact"
          clearable
          clear-icon="ri-close-line"
          style="min-inline-size: 200px; max-inline-size: 240px;"
        />

        <VSpacer />

        <VTextField
          v-model="search"
          placeholder="Search team"
          density="compact"
          style="min-inline-size: 200px; max-inline-size: 240px;"
        />
        <VBtn
          prepend-icon="ri-user-add-line"
          @click="startCreate"
        >
          Add Member
        </VBtn>
      </VCardText>

      <!-- SECTION datatable -->
      <VDataTable
        v-model:items-per-page="itemsPerPage"
        v-model:model-value="selectedRows"
        v-model:page="page"
        :items="filteredMembers"
        item-value="id"
        :headers="headers"
        :loading="loading"
        show-select
        class="text-no-wrap rounded-0"
      >
        <!-- Miembro -->
        <template #item.member="{ item }">
          <div class="d-flex align-center">
            <VAvatar
              size="34"
              variant="tonal"
              color="primary"
              class="me-3"
            >
              <span>{{ avatarText(fullName(item)) }}</span>
            </VAvatar>

            <div class="d-flex flex-column">
              <span class="text-base font-weight-medium">
                {{ fullName(item) }}
                <span
                  v-if="item.id === actorId"
                  class="text-medium-emphasis font-weight-regular"
                >(you)</span>
              </span>
              <span class="text-sm text-medium-emphasis">{{ memberSubtitle(item) }}</span>
            </div>
          </div>
        </template>

        <!-- Rol en el portal -->
        <template #item.role="{ item }">
          <!--
            El chip dice el rol; el tooltip, qué implica. Un "Manager" a secas
            obliga a acordarse de memoria qué podía tocar, y eso es justo lo que
            hay que saber para decidir si está bien puesto.

            `VTooltip` de la plantilla y no el `title` nativo: el texto son varios
            renglones con ✓ y ✕, y el tooltip del navegador los aplana y tarda un
            segundo largo en aparecer.
          -->
          <VChip
            v-if="item.canLogin"
            :color="item.role === 'owner' ? 'primary' : 'secondary'"
            size="small"
          >
            {{ roleLabel(item) }}

            <VTooltip
              activator="parent"
              location="bottom start"
              max-width="360"
            >
              <div class="bt-role-tooltip">{{ providerRoleTooltip(item.role) }}</div>
            </VTooltip>
          </VChip>
          <span
            v-else
            class="text-disabled"
          >—</span>
        </template>

        <!-- Estado -->
        <template #item.status="{ item }">
          <VChip
            :color="statusChip(item).color"
            size="small"
          >
            {{ statusChip(item).label }}
          </VChip>
        </template>

        <!-- Centros -->
        <template #item.centers="{ item }">
          <span
            v-if="seesAllCenters(item)"
            class="text-medium-emphasis"
          >All (by role)</span>
          <span
            v-else-if="centersOf(item).length"
            class="text-high-emphasis"
          >{{ centersOf(item).join(', ') }}</span>
          <span
            v-else
            class="text-disabled"
          >—</span>
        </template>

        <!-- Editar queda afuera; transferir y quitar van al menú. Un ícono
             destructivo suelto entre dos que no lo son es fácil de tocar sin
             querer, y transferir la cuenta es irreversible. -->
        <template #item.actions="{ item }">
          <IconBtn
            v-if="canEdit(item)"
            size="small"
            title="Edit member"
            aria-label="Edit member"
            @click="startEdit(item)"
          >
            <VIcon icon="ri-edit-box-line" />
          </IconBtn>

          <IconBtn
            v-if="canEdit(item) || canTransferTo(item)"
            size="small"
            color="medium-emphasis"
            title="More actions"
            aria-label="More actions"
          >
            <VIcon
              size="24"
              icon="ri-more-2-line"
            />

            <VMenu activator="parent">
              <VList>
                <VListItem
                  v-if="canTransferTo(item)"
                  :disabled="transferSubmitting && transferTarget === item.id"
                  @click="doTransfer(item)"
                >
                  <template #prepend>
                    <VIcon icon="ri-arrow-left-right-line" />
                  </template>
                  <VListItemTitle>Make owner</VListItemTitle>
                </VListItem>
                <VListItem
                  v-if="canRemove(item)"
                  class="text-error"
                  @click="remove(item)"
                >
                  <template #prepend>
                    <VIcon icon="ri-delete-bin-7-line" />
                  </template>
                  <VListItemTitle>Remove from team</VListItemTitle>
                </VListItem>
              </VList>
            </VMenu>
          </IconBtn>
        </template>

        <template #no-data>
          <div class="text-center py-8">
            <p class="text-body-1 mb-4">
              No one matches these filters.
            </p>
            <VBtn
              prepend-icon="ri-user-add-line"
              @click="startCreate"
            >
              Add a team member
            </VBtn>
          </div>
        </template>

        <!-- Mismo paginado que la libreta de clientes: selector de filas,
             rango y dos flechas sin borde. -->
        <template #bottom>
          <VDivider />

          <div class="d-flex justify-end flex-wrap gap-x-6 px-2 py-1">
            <div class="d-flex align-center gap-x-2 text-medium-emphasis text-base">
              Rows Per Page:
              <VSelect
                v-model="itemsPerPage"
                class="per-page-select"
                variant="plain"
                :items="[10, 20, 25, 50, 100]"
              />
            </div>

            <p class="d-flex align-center text-base text-high-emphasis me-2 mb-0">
              {{ paginationMeta({ page, itemsPerPage }, filteredMembers.length) }}
            </p>

            <div class="d-flex gap-x-2 align-center me-2">
              <VBtn
                class="flip-in-rtl"
                icon="ri-arrow-left-s-line"
                variant="text"
                density="comfortable"
                color="high-emphasis"
                :disabled="page <= 1"
                @click="page <= 1 ? page = 1 : page--"
              />

              <VBtn
                class="flip-in-rtl"
                icon="ri-arrow-right-s-line"
                density="comfortable"
                variant="text"
                color="high-emphasis"
                :disabled="page >= Math.ceil(filteredMembers.length / itemsPerPage)"
                @click="page >= Math.ceil(filteredMembers.length / itemsPerPage) ? page = Math.ceil(filteredMembers.length / itemsPerPage) : page++"
              />
            </div>
          </div>
        </template>

      </VDataTable>
      <!-- !SECTION -->
    </VCard>

    <MemberFormModal
      :show="showForm"
      :member="formMember"
      :actor-role="actorRole || 'manager'"
      :dive-centers="diveCenters"
      :submitting="formSubmitting"
      :error="formError"
      @close="showForm = false"
      @submit="submitForm"
    />

    <ConfirmDialog ref="confirmRef" />
  </section>
</template>

<style scoped>
/*
 * El tooltip del rol viene armado como texto con saltos de línea (una fila por
 * capacidad). `pre-line` los respeta sin tener que armar un `<ul>` desde el
 * script: lo que se muestra es una lista corta, no un documento.
 */
.bt-role-tooltip {
  white-space: pre-line;
}
</style>
